import { useParams, Link, useOutletContext, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CASE_STUDIES, findCaseStudy } from "@/data/caseStudies";

export default function CaseStudy() {
  const { slug } = useParams();
  const c = findCaseStudy(slug);
  const { onStartProject } = useOutletContext();
  if (!c) return <Navigate to="/work" replace />;

  const idx = CASE_STUDIES.findIndex((x) => x.slug === slug);
  const next = CASE_STUDIES[(idx + 1) % CASE_STUDIES.length];

  return (
    <article className="pt-40 md:pt-52 pb-16" data-testid={`case-${c.slug}`}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-stone-muted">
          [ {c.index} ] · {c.category} · {c.year}
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 font-display text-6xl md:text-[10rem] leading-[0.88] tracking-tightest text-charcoal"
        >
          {c.title}.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 max-w-2xl font-serif italic text-xl md:text-2xl leading-snug text-charcoal/80"
        >
          {c.subtitle}
        </motion.p>

        {/* Meta strip */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-charcoal/25 pt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-stone-muted">
          <Meta label="Sector" value={c.sector} />
          <Meta label="Location" value={c.location} />
          <Meta label="Year" value={c.year} />
          <Meta label="Services" value={c.services.join(" · ")} />
        </div>
      </div>

      {/* Hero image */}
      <div className="mt-24 mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="spotlight frame aspect-[16/10] bg-cream"
        >
          <img src={c.cover} alt={c.title} className="w-full h-full object-cover" />
        </motion.div>
      </div>

      {/* Editorial body */}
      <div className="mt-32 mx-auto max-w-[1200px] px-6 md:px-12 grid grid-cols-12 gap-8">
        <p className="col-span-2 font-mono text-[11px] uppercase tracking-[0.28em] text-stone-muted">The lead</p>
        <p className="col-span-12 md:col-span-9 font-serif text-2xl md:text-3xl leading-snug text-charcoal">
          {c.lead}
        </p>
      </div>

      {/* Palette & Stat */}
      <div className="mt-32 mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-12 gap-8 items-end">
        <div className="col-span-12 md:col-span-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-stone-muted">Palette</p>
          <div className="mt-6 flex gap-1">
            {c.palette.map((p) => (
              <div key={p} className="h-40 flex-1 border border-charcoal/10" style={{ background: p }}>
                <p className="pt-2 pl-2 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/70 mix-blend-difference">
                  {p}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-12 md:col-span-6 md:pl-16 border-l border-charcoal/25">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-stone-muted">Outcome</p>
          <p className="mt-6 font-display text-6xl md:text-8xl tracking-tightest leading-none text-charcoal">{c.stat}</p>
          <p className="mt-4 text-stone-muted font-mono text-[11px] uppercase tracking-[0.24em]">{c.statLabel}</p>
          <p className="mt-6 max-w-md text-charcoal/70 leading-relaxed">{c.outcome}</p>
        </div>
      </div>

      {/* Next */}
      <div className="mt-40 border-t border-charcoal/25 mx-auto max-w-[1600px] px-6 md:px-12 pt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-stone-muted">Next chapter</p>
          <Link to={`/work/${next.slug}`} className="mt-4 block group" data-testid="case-next">
            <h3 className="font-display text-5xl md:text-8xl tracking-tightest leading-none group-hover:italic transition-all duration-500">
              {next.title} →
            </h3>
          </Link>
        </div>
        <button
          onClick={onStartProject}
          className="border border-charcoal bg-charcoal text-ivory px-7 py-4 text-[11px] uppercase tracking-[0.22em] hover:bg-ivory hover:text-charcoal transition-colors self-start md:self-end"
          data-testid="case-start-project"
        >
          Start a project ↗
        </button>
      </div>
    </article>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-stone-muted/70 mb-2">{label}</p>
      <p className="text-charcoal normal-case tracking-normal font-serif text-base">{value}</p>
    </div>
  );
}
